import {
  sessionResponseSchema,
  type DeleteAccountRequest,
  type RegisterRequest,
  type SessionResponse,
  type SignInRequest,
} from '@soe/protocol';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiError, onUnauthorized, setCsrfToken } from '../api/client.js';

export type SessionState =
  { status: 'loading' } | { status: 'ready'; session: SessionResponse } | { status: 'unreachable' };

interface SessionActions {
  /** Re-checks the guest pass with the server (getting one if this browser has none). */
  refresh: () => Promise<void>;
  /** "Forget this browser": the server removes the guest; a fresh pass is issued after. */
  forget: () => Promise<void>;
  /** Adds a username and password to this guest, to save their wins (ADR 0009). */
  register: (input: RegisterRequest) => Promise<void>;
  /** Signs this browser in to a registered player. */
  signIn: (input: SignInRequest) => Promise<void>;
  /** Signs out; the browser carries on as a new guest. */
  signOut: () => Promise<void>;
  /** Deletes this registered player for good; the browser carries on as a new guest. */
  deleteAccount: (input: DeleteAccountRequest) => Promise<void>;
}

const SessionContext = createContext<(SessionActions & { state: SessionState }) | null>(null);

/**
 * Guest identity (ADR 0008). There are no accounts: on first visit the browser asks the
 * server for a guest pass, and the session cookie keeps it from then on. The pass is
 * re-checked on load, when the tab becomes visible again, and whenever a call returns 401
 * (for example after "Forget this browser" in another tab), in which case a fresh pass is
 * obtained quietly.
 */
export function SessionProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<SessionState>({ status: 'loading' });
  const inFlight = useRef<Promise<void> | null>(null);

  const adopt = useCallback((raw: unknown) => {
    const session = sessionResponseSchema.parse(raw);
    setCsrfToken(session.csrfToken);
    setState({ status: 'ready', session });
  }, []);

  const refresh = useCallback(() => {
    // One check at a time: a visibility change and a 401 can arrive together.
    inFlight.current ??= (async () => {
      try {
        try {
          adopt(await api('GET', '/session'));
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 401)) throw err;
          setCsrfToken(null);
          adopt(await api('POST', '/guest', {}));
        }
      } catch {
        setState((prev) => (prev.status === 'ready' ? prev : { status: 'unreachable' }));
      } finally {
        inFlight.current = null;
      }
    })();
    return inFlight.current;
  }, [adopt]);

  useEffect(() => {
    void refresh();
    const controller = new AbortController();
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'visible') void refresh();
      },
      { signal: controller.signal },
    );
    // Any 401 means this browser's pass has ended (forgotten here or in another tab): fetch a
    // fresh one quietly. No loading flash, so whatever screen is open stays put.
    const unsubscribe = onUnauthorized(() => {
      setCsrfToken(null);
      void refresh();
    });
    return () => {
      controller.abort();
      unsubscribe();
    };
  }, [refresh]);

  const value = useMemo(
    () => ({
      state,
      refresh,
      forget: async () => {
        await api('POST', '/guest/forget', { confirm: 'FORGET' });
        setCsrfToken(null);
        // Straight to a fresh pass, with no loading flash: Settings stays on screen to confirm.
        await refresh();
      },
      register: async (input: RegisterRequest) => {
        adopt(await api('POST', '/account/register', input));
      },
      signIn: async (input: SignInRequest) => {
        adopt(await api('POST', '/account/sign-in', input));
      },
      signOut: async () => {
        await api('POST', '/account/sign-out', {});
        setCsrfToken(null);
        await refresh();
      },
      deleteAccount: async (input: DeleteAccountRequest) => {
        await api('POST', '/account/delete', input);
        setCsrfToken(null);
        await refresh();
      },
    }),
    [state, refresh, adopt],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession must be used inside SessionProvider');
  return ctx;
}
